import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  ArrowLeft, Save, Palette, Image as ImageIcon, Crown, 
  Loader2, Upload, X, Eye, Check
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import useAuthStore from '../store/authStore';
import axiosInstance from '../api/axios';
import { toast } from 'sonner';

// Preset color themes
const COLOR_PRESETS = [
  { name: 'Standard', primary: '#8B7355', secondary: '#F5F5DC', accent: '#D4A574' },
  { name: 'Modern Blau', primary: '#2563EB', secondary: '#EFF6FF', accent: '#3B82F6' },
  { name: 'Elegant Grün', primary: '#059669', secondary: '#ECFDF5', accent: '#10B981' },
  { name: 'Warm Orange', primary: '#EA580C', secondary: '#FFF7ED', accent: '#F97316' },
  { name: 'Royal Lila', primary: '#7C3AED', secondary: '#F5F3FF', accent: '#8B5CF6' },
  { name: 'Rose Gold', primary: '#BE185D', secondary: '#FDF2F8', accent: '#EC4899' },
  { name: 'Minimalist Grau', primary: '#374151', secondary: '#F9FAFB', accent: '#6B7280' },
  { name: 'Natur Braun', primary: '#78350F', secondary: '#FFFBEB', accent: '#A16207' },
];

const VendorShopCustomization = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, isHydrated, forceHydrate } = useAuthStore();
  const fileInputRef = useRef(null);
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [isGold, setIsGold] = useState(false);
  const [shopPreview, setShopPreview] = useState(null);
  
  const [customization, setCustomization] = useState({
    banner_url: '',
    primary_color: '#8B7355',
    secondary_color: '#F5F5DC',
    accent_color: '#D4A574',
    custom_css: ''
  });

  useEffect(() => {
    forceHydrate();
  }, [forceHydrate]);

  useEffect(() => {
    if (!isHydrated) return;
    
    if (!isAuthenticated) {
      navigate('/login?redirect=/vendor-dashboard/shop-customization');
      return;
    }
    
    if (user?.role !== 'vendor') {
      toast.error('Nur für Verkäufer zugänglich');
      navigate('/');
      return;
    }
    
    loadData();
  }, [isAuthenticated, user, navigate, isHydrated]);

  const loadData = async () => {
    try {
      // Check Gold status
      const subResponse = await axiosInstance.get('/vendor-subscriptions/my-subscription');
      setIsGold(subResponse.data?.is_gold || false);
      
      // Load current customization
      const profileResponse = await axiosInstance.get('/vendors/me');
      const vendorProfile = profileResponse.data?.vendor_profile || {};
      
      if (vendorProfile.shop_customization) {
        setCustomization({
          banner_url: vendorProfile.shop_customization.banner_url || '',
          primary_color: vendorProfile.shop_customization.primary_color || '#8B7355',
          secondary_color: vendorProfile.shop_customization.secondary_color || '#F5F5DC',
          accent_color: vendorProfile.shop_customization.accent_color || '#D4A574',
          custom_css: vendorProfile.shop_customization.custom_css || ''
        });
      }
      
      setShopPreview(profileResponse.data);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  };

  const handleBannerUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate
    const validTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast.error('Nur JPG, PNG oder WebP erlaubt');
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Datei zu groß (max. 5MB)');
      return;
    }
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axiosInstance.post('/uploads/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (response.data?.url) {
        setCustomization({ ...customization, banner_url: response.data.url });
        toast.success('Banner hochgeladen');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Upload fehlgeschlagen');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axiosInstance.put('/vendors/me/customization', customization);
      toast.success('Shop-Design gespeichert!');
    } catch (error) {
      console.error('Save error:', error);
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const applyPreset = (preset) => {
    setCustomization({
      ...customization,
      primary_color: preset.primary,
      secondary_color: preset.secondary,
      accent_color: preset.accent
    });
    toast.success(`Farbschema "${preset.name}" angewendet`);
  };

  const getBannerUrl = () => {
    if (!customization.banner_url) return null;
    if (customization.banner_url.startsWith('/api')) {
      return `${process.env.REACT_APP_BACKEND_URL}${customization.banner_url}`;
    }
    return customization.banner_url;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#8B7355]" />
      </div>
    );
  }

  // Non-Gold users see upgrade prompt
  if (!isGold) {
    return (
      <div className="min-h-screen bg-gray-50">
        <header className="bg-white border-b sticky top-0 z-10">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center gap-4">
              <Link to="/vendor-dashboard">
                <Button variant="ghost" size="sm">
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Zurück
                </Button>
              </Link>
              <div>
                <h1 className="text-xl font-bold">Shop-Design anpassen</h1>
              </div>
            </div>
          </div>
        </header>

        <main className="max-w-2xl mx-auto px-4 py-16">
          <Card className="text-center">
            <CardContent className="pt-12 pb-12">
              <div className="w-20 h-20 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Crown className="h-10 w-10 text-yellow-600" />
              </div>
              <h2 className="text-2xl font-bold mb-4">Gold-Feature</h2>
              <p className="text-gray-600 mb-6 max-w-md mx-auto">
                Mit einem Gold-Abo können Sie Ihren Shop individuell gestalten:
              </p>
              <ul className="text-left max-w-xs mx-auto space-y-3 mb-8">
                <li className="flex items-center gap-2">
                  <Check className="h-5 w-5 text-green-500" />
                  <span>Eigenes Shop-Banner</span>
                </li>
                <li className="flex items-center gap-2">
                  <Check className="h-5 w-5 text-green-500" />
                  <span>Individuelle Farben</span>
                </li>
                <li className="flex items-center gap-2">
                  <Check className="h-5 w-5 text-green-500" />
                  <span>8 Farbvorlagen</span>
                </li>
                <li className="flex items-center gap-2">
                  <Check className="h-5 w-5 text-green-500" />
                  <span>Bis zu 200 Produkte</span>
                </li>
                <li className="flex items-center gap-2">
                  <Check className="h-5 w-5 text-green-500" />
                  <span>Nur 6% Provision</span>
                </li>
              </ul>
              <div className="space-y-3">
                <p className="text-3xl font-bold text-yellow-600">4,99€ <span className="text-lg font-normal text-gray-500">/ Monat</span></p>
                <Button className="bg-yellow-500 hover:bg-yellow-600 text-white px-8">
                  <Crown className="h-4 w-4 mr-2" />
                  Jetzt upgraden
                </Button>
              </div>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/vendor-dashboard">
                <Button variant="ghost" size="sm">
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Zurück
                </Button>
              </Link>
              <div>
                <h1 className="text-xl font-bold flex items-center gap-2">
                  Shop-Design anpassen
                  <Badge className="bg-yellow-500">
                    <Crown className="h-3 w-3 mr-1" />
                    Gold
                  </Badge>
                </h1>
                <p className="text-sm text-gray-600">Gestalten Sie Ihren Shop individuell</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Link to={`/shop/${user?.id}`} target="_blank">
                <Button variant="outline" size="sm">
                  <Eye className="h-4 w-4 mr-2" />
                  Vorschau
                </Button>
              </Link>
              <Button onClick={handleSave} disabled={saving}>
                {saving ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                Speichern
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        {/* Banner Upload */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ImageIcon className="h-5 w-5" />
              Shop-Banner
            </CardTitle>
            <CardDescription>
              Empfohlene Größe: 1200 x 300 Pixel (4:1 Verhältnis)
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Banner Preview */}
            <div 
              className="relative w-full h-40 rounded-lg overflow-hidden border-2 border-dashed border-gray-300"
              style={{ backgroundColor: customization.secondary_color }}
            >
              {customization.banner_url ? (
                <>
                  <img
                    src={getBannerUrl()}
                    alt="Shop Banner"
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                  <button
                    onClick={() => setCustomization({ ...customization, banner_url: '' })}
                    className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1.5 hover:bg-red-600"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </>
              ) : (
                <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-400">
                  <ImageIcon className="h-12 w-12 mb-2" />
                  <span>Kein Banner hochgeladen</span>
                </div>
              )}
            </div>
            
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={handleBannerUpload}
              className="hidden"
              id="banner-upload"
            />
            <Button
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="w-full"
            >
              {uploading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Wird hochgeladen...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  Banner hochladen
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Color Presets */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Palette className="h-5 w-5" />
              Farbvorlagen
            </CardTitle>
            <CardDescription>
              Wählen Sie ein vorgefertigtes Farbschema
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {COLOR_PRESETS.map((preset) => (
                <button
                  key={preset.name}
                  onClick={() => applyPreset(preset)}
                  className={`p-3 rounded-lg border-2 hover:border-gray-400 transition-colors ${
                    customization.primary_color === preset.primary
                      ? 'border-blue-500 ring-2 ring-blue-200'
                      : 'border-gray-200'
                  }`}
                >
                  <div className="flex gap-1 mb-2">
                    <div 
                      className="w-6 h-6 rounded-full border"
                      style={{ backgroundColor: preset.primary }}
                    />
                    <div 
                      className="w-6 h-6 rounded-full border"
                      style={{ backgroundColor: preset.secondary }}
                    />
                    <div 
                      className="w-6 h-6 rounded-full border"
                      style={{ backgroundColor: preset.accent }}
                    />
                  </div>
                  <p className="text-xs font-medium text-center">{preset.name}</p>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Custom Colors */}
        <Card>
          <CardHeader>
            <CardTitle>Eigene Farben</CardTitle>
            <CardDescription>
              Passen Sie die Farben individuell an
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="primary">Hauptfarbe</Label>
                <div className="flex gap-2 mt-1">
                  <input
                    type="color"
                    id="primary"
                    value={customization.primary_color}
                    onChange={(e) => setCustomization({ ...customization, primary_color: e.target.value })}
                    className="w-12 h-10 rounded border cursor-pointer"
                  />
                  <Input
                    value={customization.primary_color}
                    onChange={(e) => setCustomization({ ...customization, primary_color: e.target.value })}
                    placeholder="#8B7355"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">Buttons, Links, Überschriften</p>
              </div>
              
              <div>
                <Label htmlFor="secondary">Hintergrundfarbe</Label>
                <div className="flex gap-2 mt-1">
                  <input
                    type="color"
                    id="secondary"
                    value={customization.secondary_color}
                    onChange={(e) => setCustomization({ ...customization, secondary_color: e.target.value })}
                    className="w-12 h-10 rounded border cursor-pointer"
                  />
                  <Input
                    value={customization.secondary_color}
                    onChange={(e) => setCustomization({ ...customization, secondary_color: e.target.value })}
                    placeholder="#F5F5DC"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">Hintergrund, Karten</p>
              </div>
              
              <div>
                <Label htmlFor="accent">Akzentfarbe</Label>
                <div className="flex gap-2 mt-1">
                  <input
                    type="color"
                    id="accent"
                    value={customization.accent_color}
                    onChange={(e) => setCustomization({ ...customization, accent_color: e.target.value })}
                    className="w-12 h-10 rounded border cursor-pointer"
                  />
                  <Input
                    value={customization.accent_color}
                    onChange={(e) => setCustomization({ ...customization, accent_color: e.target.value })}
                    placeholder="#D4A574"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">Highlights, Badges</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Live Preview */}
        <Card>
          <CardHeader>
            <CardTitle>Live-Vorschau</CardTitle>
          </CardHeader>
          <CardContent>
            <div 
              className="rounded-lg overflow-hidden border"
              style={{ backgroundColor: customization.secondary_color }}
            >
              {/* Mini Banner */}
              <div 
                className="h-20 w-full"
                style={{ 
                  backgroundColor: customization.primary_color,
                  backgroundImage: customization.banner_url ? `url(${getBannerUrl()})` : 'none',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center'
                }}
              />
              
              {/* Content Preview */}
              <div className="p-4">
                <h3 
                  className="text-lg font-bold mb-2"
                  style={{ color: customization.primary_color }}
                >
                  {shopPreview?.vendor_profile?.shop_name || 'Ihr Shop-Name'}
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  Dies ist eine Vorschau Ihres Shops mit den gewählten Farben.
                </p>
                <div className="flex gap-2">
                  <button
                    className="px-4 py-2 rounded-lg text-white text-sm"
                    style={{ backgroundColor: customization.primary_color }}
                  >
                    Beispiel-Button
                  </button>
                  <button
                    className="px-4 py-2 rounded-lg text-sm border"
                    style={{ 
                      borderColor: customization.accent_color,
                      color: customization.accent_color
                    }}
                  >
                    Akzent-Button
                  </button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default VendorShopCustomization;
